import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { RecognizedTimezones, getLocalTimezone } from '@modernloop/shared/datetime';

const LOCAL_STORAGE_KEY = 'DISPLAY_TIMEZONE';

type DisplayTimezoneContextValue = [string, (timezone: string) => void];

const defaultContextValue: DisplayTimezoneContextValue = [getLocalTimezone(), () => {}];

const DisplayTimezoneContext = createContext<DisplayTimezoneContextValue>(defaultContextValue);

interface DisplayTimezoneProviderProps {
  children?: ReactNode;
}

/**
 * Validates if a timezone string is a recognized timezone
 * @param timezone - The timezone string to validate
 * @returns true if the timezone is valid, false otherwise
 */
const isValidTimezone = (timezone: unknown): timezone is string => {
  if (typeof timezone !== 'string' || timezone.trim() === '') {
    return false;
  }
  return RecognizedTimezones.includes(timezone);
};

/**
 * Safely parses and validates timezone from localStorage
 * @returns The stored timezone, or local timezone if invalid/not found
 */
const restoreTimezone = (): string => {
  try {
    const storedData = window.localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!storedData) {
      return getLocalTimezone();
    }

    const parsed: unknown = JSON.parse(storedData);

    // Validate that parsed data is a valid timezone string
    if (!isValidTimezone(parsed)) {
      console.warn('DisplayTimezoneProvider: Invalid timezone in localStorage, using local timezone');
      return getLocalTimezone();
    }

    return parsed;
  } catch (err) {
    // JSON.parse failed or other error
    console.warn('DisplayTimezoneProvider: Failed to parse localStorage data', err);
    return getLocalTimezone();
  }
};

/**
 * Safely stores timezone to localStorage
 * @param timezone - The timezone string to store
 */
const storeTimezone = (timezone: string): void => {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(timezone));
  } catch (err) {
    // localStorage might be full or disabled
    console.warn('DisplayTimezoneProvider: Failed to save to localStorage', err);
  }
};

/**
 * Provider component for managing display timezone with localStorage persistence.
 * The display timezone is used to format dates and times throughout the application.
 *
 * @example
 * ```tsx
 * <DisplayTimezoneProvider>
 *   <App />
 * </DisplayTimezoneProvider>
 * ```
 */
export const DisplayTimezoneProvider: FC<DisplayTimezoneProviderProps> = ({ children }) => {
  const [timezone, setTimezoneState] = useState<string>(getLocalTimezone);

  // Initialize from localStorage on mount
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    const restored = restoreTimezone();
    setTimezoneState(restored);
  }, []);

  const setTimezone = useCallback((newTimezone: string) => {
    // Validate the timezone before setting
    if (!isValidTimezone(newTimezone)) {
      console.warn(`DisplayTimezoneProvider: Attempted to set invalid timezone "${newTimezone}"`);
      return;
    }

    setTimezoneState(newTimezone);
    storeTimezone(newTimezone);
  }, []);

  const value = useMemo<DisplayTimezoneContextValue>(() => [timezone, setTimezone], [timezone, setTimezone]);

  return <DisplayTimezoneContext.Provider value={value}>{children}</DisplayTimezoneContext.Provider>;
};

/**
 * Hook to access and manage display timezone.
 *
 * @returns A tuple containing:
 * - `displayTimezone`: The current display timezone string (never null/undefined, defaults to local timezone)
 * - `setDisplayTimezone`: Function to set the display timezone (validates against recognized timezones)
 *
 * @example
 * ```tsx
 * const [displayTimezone, setDisplayTimezone] = useDisplayTimezone();
 *
 * // Get current timezone
 * console.log(displayTimezone); // e.g., 'America/Los_Angeles'
 *
 * // Set a new timezone
 * setDisplayTimezone('America/New_York');
 * ```
 */
export const useDisplayTimezone = (): DisplayTimezoneContextValue => {
  const context = useContext(DisplayTimezoneContext);

  if (context === undefined) {
    // Return safe default if used outside provider (shouldn't happen but be resilient)
    console.warn('useDisplayTimezone must be used within a DisplayTimezoneProvider');
    return defaultContextValue;
  }

  return context;
};
